import React, { createContext, useContext, useEffect, useState } from 'react';

export type Team = 'hornets' | 'panthers';
export type Mode = 'light' | 'dark';
export type ModePreference = Mode | 'system';

interface ThemeContextType {
  team: Team;
  mode: Mode;
  modePreference: ModePreference;
  setTeam: (team: Team) => void;
  setModePreference: (pref: ModePreference) => void;
  cycleTeam: () => void;
  cycleMode: () => void;

  /** Legacy surface for components that still toggle light/dark only. */
  theme: Mode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const TEAM_KEY = 'theme-team';
const MODE_KEY = 'theme-mode';
const LEGACY_KEY = 'theme-preference';

const isBrowser = typeof window !== 'undefined';

const getSystemMode = (): Mode =>
  isBrowser && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const loadTeam = (): Team => {
  if (!isBrowser) return 'hornets';
  const stored = localStorage.getItem(TEAM_KEY);
  return stored === 'panthers' ? 'panthers' : 'hornets';
};

const loadModePreference = (): ModePreference => {
  if (!isBrowser) return 'system';
  const stored = localStorage.getItem(MODE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (legacy === 'light' || legacy === 'dark' || legacy === 'system') return legacy;
  return 'system';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [team, setTeamState] = useState<Team>(loadTeam);
  const [modePreference, setModePreferenceState] = useState<ModePreference>(loadModePreference);
  const [mode, setMode] = useState<Mode>(() =>
    modePreference === 'system' ? getSystemMode() : modePreference
  );

  useEffect(() => {
    const resolved = modePreference === 'system' ? getSystemMode() : modePreference;
    setMode(resolved);
    const root = document.documentElement;
    root.setAttribute('data-team', team);
    root.setAttribute('data-mode', resolved);
    // Keep legacy class for any untouched components still reading it.
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    localStorage.setItem(TEAM_KEY, team);
    localStorage.setItem(MODE_KEY, modePreference);
    localStorage.removeItem(LEGACY_KEY);
  }, [team, modePreference]);

  useEffect(() => {
    if (modePreference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = (e: MediaQueryListEvent) => {
      const next: Mode = e.matches ? 'dark' : 'light';
      setMode(next);
      const root = document.documentElement;
      root.setAttribute('data-mode', next);
      root.classList.remove('light', 'dark');
      root.classList.add(next);
    };
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, [modePreference]);

  const setTeam = (next: Team) => setTeamState(next);
  const setModePreference = (next: ModePreference) => setModePreferenceState(next);
  const cycleTeam = () => setTeamState(t => (t === 'hornets' ? 'panthers' : 'hornets'));
  const cycleMode = () => {
    const order: ModePreference[] = ['light', 'dark', 'system'];
    const idx = order.indexOf(modePreference);
    setModePreferenceState(order[(idx + 1) % order.length]);
  };
  const toggleTheme = () => {
    setModePreferenceState(mode === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider
      value={{
        team,
        mode,
        modePreference,
        setTeam,
        setModePreference,
        cycleTeam,
        cycleMode,
        theme: mode,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
